import { Badge } from '@fluentui/react-components';
import { INSTANCE_STATUS_LABELS, REMOTE_ACCESS_LABELS, SSO_STATE_LABELS, type InstanceStatus, type RemoteAccess, type SsoState } from '@vxh/shared';

const COLORS: Record<InstanceStatus, 'success' | 'danger' | 'informative' | 'subtle'> = { online: 'success', offline: 'danger', never: 'subtle', disabled: 'subtle' };

/** Online / offline / not connected / switched off, as a coloured pill (and in words, never colour alone). */
export function StatusPill({ status }: { status: InstanceStatus }) {
  return (
    <Badge appearance={status === 'online' ? 'tint' : 'outline'} color={COLORS[status]} shape="rounded" style={{ whiteSpace: 'nowrap' }}>
      {INSTANCE_STATUS_LABELS[status]}
    </Badge>
  );
}

const REMOTE_COLORS: Record<RemoteAccess, 'success' | 'informative' | 'subtle'> = { connected: 'success', off: 'informative', offline: 'subtle' };

/** Whether people can open the instance through the hub right now (its tunnel is up), or why not. */
export function RemotePill({ remote }: { remote: RemoteAccess }) {
  return (
    <Badge appearance={remote === 'connected' ? 'tint' : 'outline'} color={REMOTE_COLORS[remote]} shape="rounded" style={{ whiteSpace: 'nowrap' }}>
      {REMOTE_ACCESS_LABELS[remote]}
    </Badge>
  );
}

const SSO_COLORS: Record<SsoState, 'success' | 'informative' | 'subtle'> = { on: 'success', off_instance: 'informative', off_hub: 'informative', unavailable: 'subtle' };
/** What each state means for the person, in plain words (shown as the pill's tooltip). */
export const SSO_STATE_HELP: Record<SsoState, string> = {
  on: 'People with access sign in to this installation with their hub account, with no second sign-in.',
  off_instance: 'This installation has single sign-on, but its administrator has not switched on "Voxshift Hub" sign-in yet. Until then people use its own login.',
  off_hub: 'Single sign-on is switched off for this instance in the hub (Edit details).',
  unavailable: 'This installation has not reported single sign-on: it runs a release that does not have it yet. Update it, then it will show here. Until then people use its own login.',
};

/** Whether people can sign in to the instance with their hub account right now, or why not. */
export function SsoPill({ state }: { state: SsoState }) {
  return (
    <Badge appearance={state === 'on' ? 'tint' : 'outline'} color={SSO_COLORS[state]} shape="rounded" style={{ whiteSpace: 'nowrap' }} title={SSO_STATE_HELP[state]}>
      {SSO_STATE_LABELS[state]}
    </Badge>
  );
}
